import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { FooterComponent } from './footer/footer.component';
import { HeaderComponent } from './header/header.component';
import { SpinnerFullPageComponent } from './shared/spinner-full-page/spinner-full-page.component';
import { CommonModule } from '@angular/common';
import { UserService } from './services/user.service';
import { distinctUntilChanged, Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { RippleModule } from 'primeng/ripple';
import { CacheService } from './services/cache.service';
import { DialogType } from './services/model/dialog-type.enum';
import { CascadeDealer } from './services/model/cascade-dealer.model';
import { DataService } from './services/data.service';
import { ExecutingTaskMonitorService } from './services/executing-task-monitor.service';
import { DealerAddress } from './services/model/dealer-address.model';
import { DealerInfo } from './services/model/dealer-info.model';
import { NotificationMessage } from './services/model/notification-message.model';
import { ReportModel } from './reports/model/report-option.model';
import { PlatformModule, Platform } from '@angular/cdk/platform';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    FooterComponent,
    HeaderComponent,
    CommonModule,
    SpinnerFullPageComponent,
    ToastModule,
    RippleModule,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  standalone: true,
  providers: [MessageService],
})
export class AppComponent implements OnInit {
  public title = 'reports-app';
  private _dealerListErrorCounter: number = 0;

  constructor(
    public userService: UserService,
    private readonly _cache: CacheService,
    private readonly _messageService: MessageService,
    private readonly _dataService: DataService,
    private readonly _executingTaskSvc: ExecutingTaskMonitorService,
    private readonly _platform: Platform
  ) {
    this._cache.showDialog.subscribe((data) => {
      let detail: string = this.getMessage(data.messages ?? []);
      switch (data.dialogType) {
        case DialogType.error:
          this._messageService.add({
            severity: 'error',
            summary: data.title,
            detail: detail,
            sticky: true,
          });
          break;
        case DialogType.info:
          this._messageService.add({
            severity: 'info',
            summary: data.title,
            detail: detail,
            life: 5000,
          });
          break;
        case DialogType.warning:
          this._messageService.add({
            severity: 'warn',
            summary: data.title,
            detail: detail,
            life: 5000,
          });
          break;
        default:
          break;
      }
    });
  }

  ngOnInit(): void {
    this.userService.authService.user$
      .pipe(distinctUntilChanged())
      .subscribe((user) => {
        this.userService.loadUserProfile(user);
        this._executingTaskSvc.executingTasks = [];
        this.getBillTos().subscribe((cascadeDealers) => {
          this._cache.billTos = cascadeDealers;
          this.setDealersForReports();

          this.getReportConfigs().subscribe((reportConfigs) => {
            this._cache.reportConfigs = reportConfigs;
            if (!this._platform.BLINK) {
              this.showWarningMessage(
                'Please note for best user experience please user Google Chrome browser version 120 or greater.',
                'Unsupported Browser.'
              );
            }
          });
        });
      });
  }

  private getReportConfigs(): Observable<ReportModel[]> {
    return this._dataService.getAll<ReportModel>(
      `report/GetReportConfiguration`,
      'Get Report Configs ...',
      false
    );
  }

  private getBillTos(): Observable<CascadeDealer[]> {
    const companyCode = this.userService.userProfile.companyCode;
    const strategicBusinessUnit = this.userService.userProfile.sbuCode;
    const commissionCode = this.userService.userProfile.commissionCode;
    const accountNumber = this.userService.userProfile.accountNumber;

    if (this.userService.userProfile.isEmployee) {
      return this._dataService.getAll<CascadeDealer>(
        `DealerSearch/GetDealersForEmployee/${companyCode}/${strategicBusinessUnit}`,
        'Get Dealers For Employee...',
        false
      );
    } else if (this.userService.userProfile.isSalesRep) {
      return this._dataService.getAll<CascadeDealer>(
        `DealerSearch/GetDealersForSalesRep/${commissionCode}/${companyCode}/${strategicBusinessUnit}`,
        'Get Dealers For Sales Rep...',
        false
      );
    } else if (this.userService.userProfile.isDealer) {
      return this._dataService.getAll<CascadeDealer>(
        `DealerSearch/GetDealerLocations/${accountNumber}/${companyCode}/${strategicBusinessUnit}`,
        'Get Locations ...',
        false
      );
    } else {
      throw Error(
        'Unable to set user type. Please refresh browser and try again later'
      );
    }
  }

  private setDealersForReports(): void {
    try {
      this._cache.dealerListForReports = [];
      this._cache?.billTos?.forEach((cascadeDealer: CascadeDealer) => {
        this.populateBillToDealerListForReports(cascadeDealer);
      });
    } catch (error) {
      console.error('setDealersForReports', error);
    } finally {
      this._dealerListErrorCounter = 0;
      this.notifyUserIfAnyErrorLoadingDealerListForReports(
        `Unable to add ${this._dealerListErrorCounter} dealers to report list.`
      );
    }
  }

  private populateBillToDealerListForReports(cascadeDealer: CascadeDealer) {
    try {
      const isFound = this._cache.dealerListForReports.findIndex(
        (f) => f.accountNumber === cascadeDealer.accountNumber
      );
      if (isFound === -1) {
        this._cache.dealerListForReports.push(
          new DealerInfo(
            cascadeDealer.accountNumber,
            `${cascadeDealer.companyName!} Bill-To`,
            cascadeDealer.commissionCode1!,
            this.getBranchPlant(cascadeDealer.companyCode!),
            cascadeDealer.companyCode!,
            'Bill-To'
          )
        );
        this.populateShipToDealerListForReports(
          cascadeDealer.shipToAddresses!,
          cascadeDealer.commissionCode1!,
          cascadeDealer.companyCode!
        );
      }
    } catch (error) {
      console.error('populateDealerListForReports', error);
      this._dealerListErrorCounter++;
    }
  }

  private populateShipToDealerListForReports(
    addresses: DealerAddress[],
    commissionCode: number,
    companyCode: string
  ): void {
    try {
      addresses.forEach((address) => {
        const isFound = this._cache.dealerListForReports.findIndex(
          (f) => f.accountNumber === address.accountNumber
        );
        if (isFound === -1) {
          const branchPlant = this.getBranchPlant(companyCode);
          this._cache.dealerListForReports.push(
            new DealerInfo(
              address.accountNumber,
              `${address.companyName} Ship-To`,
              commissionCode,
              branchPlant,
              companyCode,
              'Ship-To'
            )
          );
        }
      });
    } catch (error) {
      this._dealerListErrorCounter++;
      console.error('populateShipToDealerListForReports', error);
    }
  }

  private notifyUserIfAnyErrorLoadingDealerListForReports(
    message: string
  ): void {
    if (this._dealerListErrorCounter > 0) {
      this.showWarningMessage(message, 'Populate List');
    }
  }

  private showWarningMessage(message: string, title: string) {
    this._cache.showDialog.next(
      new NotificationMessage(DialogType.warning, title, [message])
    );
  }

  private getBranchPlant(companyCode: string): string | undefined {
    if (companyCode === undefined || companyCode === null) {
      return undefined;
    } else {
      return companyCode == '00015' ? 'REN1' : 'CDL2';
    }
  }

  private getMessage(data: string[]): string {
    let message: string = '';
    try {
      if (data != null || data != undefined) {
        if (data.length > 0) {
          for (const element of data) {
            message = `${message} ${element}`;
          }
        }
      }
    } catch (error) {
      console.error('getMessage', error);
    }
    return message;
  }
}
